import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFormFieldVisibility } from "@/context/FormFieldVisibilityContext";
import { tWithKey } from "@/lib/i18n-fallback";
import { Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Tab } from "./AbstractForm";

/**
 * Dropdown listing every hideable tab of the form with a checkbox to show/hide
 * it. Rendered under the left tab rail.
 */
export function FormTabsVisibilityMenu({ tabs }: { tabs: readonly Tab[] }) {
  const { t } = useTranslation();
  const { enabled, isTabVisible, toggleTabVisibility } =
    useFormFieldVisibility();

  // Action tabs (e.g. "Gérer les photos") are hideable too — only statically
  // hidden or explicitly non-hideable tabs are excluded.
  const hideableTabs = tabs.filter((tab) => !tab.hide && !tab.nonHideable);

  if (!enabled || hideableTabs.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2 w-full justify-start gap-2 bg-blue-50 hover:bg-blue-100"
        >
          <Eye className="h-4 w-4" />
          {tWithKey(t, "fieldVisibility.tabs.manage", "Onglets")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[70vh] w-56 overflow-y-auto"
      >
        <DropdownMenuLabel>
          {tWithKey(t, "fieldVisibility.tabs.title", "Onglets affichés")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {hideableTabs.map((tab) => (
          <DropdownMenuCheckboxItem
            key={tab.value}
            checked={isTabVisible(tab.value)}
            className="hover:bg-slate-200 focus:bg-slate-200 focus:text-accent-foreground data-[highlighted]:bg-slate-200"
            // Keep the menu open while toggling several tabs.
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={() => toggleTabVisibility(tab.value)}
          >
            {t(tab.label ?? tab.value)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
